import { useState } from 'react';
import styled from '@emotion/styled';
import { useTestContext } from '../../context/testContext';
import { Button, IconButton, PlusIcon, CheckIcon, colors } from '../../ui';
import { LabelledValue, ValueBox } from '../../screens/optician/testScreenKit';

// Contrast Sensitivity scoring - values row (Figma session/14 left):
// Present logMAR Size [-][1][+] | Contrast Sensitivity [0.48] | Contrast [52.0%] | Save Results

const Row = styled.div`
  display: grid;
  grid-template-columns: auto minmax(84px, 120px) minmax(84px, 120px) auto;
  align-items: end;
  gap: 16px;
  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Stepper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MinusGlyph = () => (
  <svg width={16} height={16} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2.5} strokeLinecap='round' aria-hidden='true'>
    <path d='M5 12h14' />
  </svg>
);

const SaveCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 44px;
  button {
    font-size: 15px;
    color: ${({ saved }) => (saved ? colors.success[600] : colors.primary)};
  }
`;

const ContrastSensitivityInfo = ({ logmarSize, updateLogmarData, contrastValue, eyeTested }) => {
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const {
    testData: { maxPermittedLogmar, contrastSensitivityResults },
    updateTestData,
  } = useTestContext();

  const saveTestResults = () => {
    const existingResults = contrastSensitivityResults || [];

    updateTestData({
      contrastSensitivityResults: [...existingResults, { logmarTested: logmarSize.value, [eyeTested]: contrastValue ? 100 / contrastValue : 0 }],
    });

    setShowSaveConfirmation(true);
    setTimeout(() => setShowSaveConfirmation(false), 1500);
  };

  const canDecrement = logmarSize?.value > -0.2;
  const canIncrement = !(logmarSize?.value === 1 || logmarSize?.id === maxPermittedLogmar?.id);

  return (
    <Row>
      <LabelledValue label='Present logMAR Size'>
        <Stepper>
          <IconButton label='Smaller logMAR' variant='outline' size={36} disabled={!canDecrement} onClick={() => updateLogmarData({ type: 'decrementLogmarSize' })}>
            <MinusGlyph />
          </IconButton>
          <ValueBox muted minWidth={64} align='center' data-testid='cs-logmar-size'>
            {logmarSize?.value?.toFixed(1)}
          </ValueBox>
          <IconButton label='Bigger logMAR' variant='outline' size={36} disabled={!canIncrement} onClick={() => updateLogmarData({ type: 'incrementLogmarSize' })}>
            <PlusIcon size={16} strokeWidth={2.5} />
          </IconButton>
        </Stepper>
      </LabelledValue>
      <LabelledValue label='Contrast Sensitivity'>
        <ValueBox data-testid='cs-sensitivity'>{contrastValue < 0.1 ? '-' : (100 / contrastValue).toFixed(1)}</ValueBox>
      </LabelledValue>
      <LabelledValue label='Contrast'>
        <ValueBox data-testid='cs-contrast'>{`${contrastValue.toFixed(1)}%`}</ValueBox>
      </LabelledValue>
      <SaveCell saved={showSaveConfirmation}>
        <Button variant='tertiary' onClick={() => saveTestResults()} aria-disabled={showSaveConfirmation} style={{ pointerEvents: showSaveConfirmation ? 'none' : 'auto' }} iconLeft={showSaveConfirmation ? <CheckIcon size={16} /> : null}>
          {showSaveConfirmation ? 'Saved' : 'Save Results'}
        </Button>
      </SaveCell>
    </Row>
  );
};

export default ContrastSensitivityInfo;
